import Image from '@/src/components/Image';
import { IStore, OrderType } from '@/src/types/stores';
import { PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { Link } from '@/src/i18n/navigation';
import { useLocale, useTranslations } from 'next-intl';
import FastIcon from '@/public/assets/Fast.svg';
import { FreeDeliveryBadge } from './FreeDeliveryBage';
import { Car, CarFront, CirclePercent, Gift, Percent, Plane, Tag, Truck } from 'lucide-react';
import { FC } from 'react';
import CurrentLanguageText from '@/src/components/helper/CurrentLanguageText';
import { Routes } from '@/src/routes/routes';
import { RenderStoreTitle } from './RenderStoreTitle';
import { Badge } from '@/src/components/ui/Badge';
import ExpressIcon from '@/src/components/icons/ExpressIcon';
import ExpressFillIcon from '@/src/components/icons/ExpressFillIcon';

type Props = {
  isFilterByLocalShipping?: boolean;
  storeContent: IStore;
  deliveryTime?: string;
  onStoreClicked?: () => void;
};

export const InstantDeliveryBadge = () => {
  const t = useTranslations('stores');

  return (
    <span className="flex h-6 items-center justify-center space-x-1 rounded-md border border-[#E7E7E8] bg-[#F3F3F3] p-2 text-black lg:p-3 rtl:space-x-reverse">
      <Image local src={FastIcon} alt="FastIcon" width={16} height={16} />
      <span className="text-[10px] font-bold">{t('Instance')}</span>
    </span>
  );
};

const StoreCard: FC<Props> = ({
  storeContent,
  isFilterByLocalShipping,
  deliveryTime,
  onStoreClicked,
}) => {
  const { englishName, distance, store, orderTypes, name, discountOfferPercentage, deliveryFees } =
    storeContent;

  const t = useTranslations('stores');
  const locale = useLocale();
  const isArabic = locale === 'ar';
  const isLocalShipping = orderTypes?.includes(OrderType?.Global);
  const isStoreSupportGift = orderTypes?.includes(OrderType?.Gift);

  const industriesText = store.industries
    ?.slice(0, 2)
    .map((item) => (isArabic ? item.arabicName : item.englishName))
    .filter(Boolean)
    .join(isArabic ? ' و ' : ' and ');

  // If deliveryTime is a range (e.g., "12-24 m" or "2-4 D"), extract only the upper bound value ("24 m", "4 D")
  const formattedDeliveryTime = deliveryTime
    ? deliveryTime.replace(/^\s*\d+\s*[-–—]\s*(\d+.*)$/, '$1').trim()
    : undefined;

  return (
    <figure className="relative flex w-full gap-2 duration-300 hover:-translate-y-1 lg:gap-3">
      <div className="aspect-square h-20 w-20 shrink-0 overflow-hidden rounded-2xl lg:h-25 lg:w-25">
        <Image
          src={store.logoURL}
          key={store.logoURL}
          size={PRODUCT_IMAGE_SIZE.MEDIUM}
          alt={englishName}
          width={100}
          height={100}
          className="h-full w-full object-cover"
        />
      </div>

      <figcaption className="flex w-[calc(100%-80px)] flex-col items-start justify-center gap-1 text-sm lg:w-[calc(100%-100px)] lg:gap-2">
        {/* heading */}
        <RenderStoreTitle>
          <h2 className="text-sm font-bold text-[#272728] md:text-base">
            <CurrentLanguageText arabicName={store.arabicName} englishName={store.englishName} />
          </h2>
        </RenderStoreTitle>

        {/* sub heading store name and destaince  */}
        <div className="flex w-full min-w-0 items-center text-xs font-medium">
          {industriesText && (
            <p className="m-0 min-w-0 truncate p-0 text-[#52526B]">{industriesText}</p>
          )}
          <span className="bg-secondary mx-2 block h-4 w-0.5 shrink-0" />

          {formattedDeliveryTime ? (
            <time className="flex shrink-0 items-center gap-1 text-xs text-[#52526B]">
              <ExpressFillIcon className="text-amber-400" />
              {formattedDeliveryTime}
            </time>
          ) : null}
          <span className="bg-gray/50 mx-1 inline-block h-1 w-1 shrink-0 rounded-full" />

          <span className="shrink-0 text-xs text-[#52526B]">
            {distance} {t('km')}
          </span>
        </div>

        <div className="flex w-full gap-2">
          {isLocalShipping && isFilterByLocalShipping ? (
            <div className="flex items-center gap-1 rounded-md border border-[#076FCE] bg-[#076ece0d] px-1 text-sm text-[#076FCE]">
              <Plane width={16} height={16} /> {t('LocalShipping')}
            </div>
          ) : (
            <div className="flex w-full items-center justify-between">
              <div className="item center flex flex-wrap gap-1">
                {deliveryFees === 0 ? (
                  <Badge
                    className="bg-secondary gap-1 px-1.5 text-xs font-bold"
                    variant={'success'}
                  >
                    {' '}
                    <CarFront className="size-3.5" /> {t('freeDelivery')}{' '}
                  </Badge>
                ) : null}

                {discountOfferPercentage && discountOfferPercentage > 0 ? (
                  <Badge className="bg-secondary gap-1 px-1.5 text-xs font-bold">
                    <span>{discountOfferPercentage}%</span>
                    {t('off')}
                  </Badge>
                ) : null}
                {/* coupon badge */}

                {isStoreSupportGift && (
                  <Badge variant="gray" className="gap-1 px-1.5 text-xs">
                    <Gift className="size-3" />
                    {t('gifting')}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
      </figcaption>
      <Link
        href={Routes.branchDetails(store?.id, store?.englishName)}
        onClick={onStoreClicked}
        className="absolute inset-0"
      />
    </figure>
  );
};

export default StoreCard;
